import {useRef, useState} from 'react';
import {Link} from 'react-router';

import {FormFieldT} from '@/lib/types/form.types';
import {speculatorInsertT, speculatorT} from '@/lib/types/speculator.types';
import {statsRowT} from '@/lib/types/stats.types';

import {dateFormatter} from '@/lib/helpers';
import {createDataTableConfig} from '@/lib/helpers/config';

import {useViewQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useSpeculators} from '@/providers/components/speculatorsProvider';

import {ConfirmDeleteModalComponent, ModalFormComponent, StatsRowComponent} from '@/components';
import Page from '@/components/Page';
import {DataTable} from '@/components/dataTable';

// form data for creating a new speculator
const initialFormData: speculatorInsertT = {
  date_created: '',
  date_modified: '',
  speculator_email: '',
  uid: '',
  created_by: '',
};

const createFields: FormFieldT<speculatorInsertT>[] = [
  {name: 'speculator_name_first', label: 'First Name', type: 'text', placeholder: 'First Name', required: true},
  {name: 'speculator_name_last', label: 'Last Name', type: 'text', placeholder: 'Last Name', required: true},
  {name: 'speculator_email', label: 'Email', type: 'email', placeholder: 'Email', required: true},
  {name: 'speculator_phone', label: 'Phone Number', type: 'tel', placeholder: 'Phone Number', required: false},
];

const editFields: FormFieldT<speculatorInsertT>[] = [
  ...createFields,
  {name: 'speculator_username', label: 'Username', type: 'text', placeholder: 'Username', required: true},
  {name: 'speculator_address', label: 'Address 1', type: 'text', placeholder: 'Address 1', required: false},
  {name: 'speculator_address2', label: 'Address 2', type: 'text', placeholder: 'Address 2', required: false},
  {name: 'speculator_city', label: 'City', type: 'text', placeholder: 'City', required: false},
  {name: 'speculator_state', label: 'State', type: 'text', placeholder: 'State', required: false},
  {name: 'speculator_postal_code', label: 'Postal Code', type: 'text', placeholder: 'Postal Code', required: false},
  {name: 'speculator_country', label: 'Country', type: 'text', placeholder: 'Country', required: false},
];

function AdminSpeculators() {
  const {isAdmin} = useAuth();
  const {deleteData, createData, updateData, getSpeculator} = useSpeculators();

  // State
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Refs
  const selectedIdRef = useRef<number | null>(null);
  const formDataRef = useRef<speculatorInsertT>(initialFormData);

  // TanStack queries
  const {data: stats, refetch: refetchStats} = useViewQuery({view: 'speculator_stats'});

  // Functions
  const handleCreate = async (data: speculatorInsertT) => {
    try {
      setIsSubmitting(true);
      await createData(data);
      refetchStats();
      setIsVisible(false);
    } catch (err) {
      setSubmissionError(`Failed to create retailer ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (data: speculatorInsertT) => {
    if (selectedIdRef.current === null) {
      setSubmissionError('No retailer selected');
      return;
    }

    try {
      setIsSubmitting(true);
      await updateData(data);
      refetchStats();
      setIsVisible(false);
    } catch (err) {
      setSubmissionError(`Failed to update retailer: ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (selectedIdRef.current === null) {
      setDeleteError('No speculator selected');
      return;
    }

    try {
      setDeleteError(null);
      setIsSubmitting(true);
      await deleteData(selectedIdRef.current);
      refetchStats();
      setIsDeleteModalVisible(false);
    } catch (err) {
      setDeleteError(`Failed to delete user ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAdd = async () => {
    formDataRef.current = initialFormData;
    setIsEditing(false);
    setIsVisible(true);
  };

  const handleEdit = async (speculatorId: number) => {
    const speculator = await getSpeculator(speculatorId);
    formDataRef.current = speculator;
    selectedIdRef.current = speculator.speculator_id;
    setIsEditing(true);
    setIsVisible(true);
  };

  const handleDeleteEvent = (speculatorId: number) => {
    selectedIdRef.current = speculatorId;
    setIsDeleteModalVisible(true);
  };

  // Data Table
  const dataTableConfig = createDataTableConfig({
    table: 'Speculator',
    title: 'Speculators Summary',
    searchColumn: 'speculator_username',
    columns: [
      'speculator_id',
      'speculator_status',
      'speculator_username',
      'speculator_name_first',
      'speculator_name_last',
      'speculator_email',
      'speculator_phone',
      'speculator_city',
      'date_created',
      'date_removed',
    ],
    hideAdd: true,
    onAdd: handleAdd,
    columnsDisplay: [
      {header: 'ID', field: 'speculator_id'},
      {
        header: 'Status',
        field: 'speculator_status',
        render: row => (
          <span className={`badge bg-${row.speculator_status === 'active' ? 'success' : 'danger'} text-white`}>
            {row.speculator_status === 'active' ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        header: 'Username',
        field: 'speculator_username',
        render: row => (
          <Link
            className="text-primary hover:text-primary dark:text-primary"
            to={`/admin/speculators/${row.speculator_id}`}
          >
            {row.speculator_username}
          </Link>
        ),
      },
      {
        header: 'Name',
        field: 'speculator_name_first',
        render: row => <p>{`${row.speculator_name_first} ${row.speculator_name_last}`}</p>,
      },
      {header: 'Email', field: 'speculator_email'},
      {header: 'Phone', field: 'speculator_phone'},
      {
        header: 'City',
        field: 'speculator_city',
      },
      {
        header: 'Created',
        field: 'date_created',
        render: row => <div>{dateFormatter(row.date_created, 'short')}</div>,
      },
    ],
    actionsDisplay: [
      {
        icon: 'ri-pencil-line',
        color: 'info',
        handler: row => handleEdit(row.speculator_id),
        permission: () => isAdmin,
      },
      {
        icon: 'ri-delete-bin-line',
        color: 'danger',
        handler: row => handleDeleteEvent(row.speculator_id),
        permission: () => isAdmin,
      },
    ],
  });

  // Stats
  const statsRow: statsRowT = [
    {
      title: 'Total Speculators',
      value: stats?.total ?? 0,
      subtitle: 'This Month',
      icon: 'ri-team-line',
      color: 'primary',
      change: 1.03,
      cardClass: 'Users-main-card primary',
    },
    {
      title: 'Active Speculators',
      value: stats?.active ?? 0,
      subtitle: 'This Month',
      icon: 'ri-user-unfollow-line',
      color: 'secondary',
      change: 0.36,
      cardClass: 'Users-main-card secondary',
    },
    {
      title: 'Inactive Speculators',
      value: stats?.inactive ?? 0,
      subtitle: 'This Month',
      icon: 'ri-service-line',
      color: 'warning',
      change: -1.28,
      cardClass: 'Users-main-card warning',
    },
  ];

  return (
    <Page currentPage="Speculators">
      <StatsRowComponent stats={statsRow} />
      <DataTable {...dataTableConfig} />
      <ModalFormComponent
        isVisible={isVisible}
        fields={isEditing ? editFields : createFields}
        formData={formDataRef.current}
        onSubmit={(data: speculatorT) => {
          setSubmissionError(null);
          if (isEditing) {
            handleUpdate(data);
          } else {
            handleCreate(data);
          }
        }}
        onClose={() => setIsVisible(false)}
        error={submissionError}
        isSubmitting={isSubmitting}
        title={isEditing ? 'Edit Speculator' : 'Create New Speculator'}
      />
      <ConfirmDeleteModalComponent
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={handleDelete}
        title="Confirm Deletion"
        description="Are you sure you want to delete this speculator? This action cannot be undone."
        isSubmitting={isSubmitting}
        error={deleteError}
      />
    </Page>
  );
}

export default AdminSpeculators;
